import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CardInfo from './CardInfo';
import { v3Api } from '../hooks/useApi';

/**
 * SynergyMap — pairwise correlation list, with top pair highlighted.
 * Initial data (`map`) comes from /api/command/:concessionaireId at the
 * sub-category grain. A grain toggle refetches /api/command/:id/synergy at
 * category or tenant grain without reloading the whole command payload.
 */
const GRAINS = [
    { key: 'subcategory', label: 'Sub-category' },
    { key: 'category', label: 'Category' },
    { key: 'tenant', label: 'Tenant' },
];

export default function SynergyMap({ map }) {
    const { t } = useTranslation();
    const [dimension, setDimension] = useState('subcategory');
    const [fetched, setFetched] = useState(null); // grain-specific override of `map`
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const active = fetched || map;
    const pairs = Array.isArray(active?.pairs) ? active.pairs : [];
    const top = active?.topPair;
    const topKey = top ? `${top.a}|${top.b}` : null;

    const switchGrain = useCallback((dim) => {
        if (loading || dim === dimension) return;
        setDimension(dim);
        setErr(null);
        // sub-category is what the parent already passed in `map` — reuse it.
        if (dim === 'subcategory') { setFetched(null); return; }
        const cid = localStorage.getItem('concessionaireId');
        if (!cid) return;
        setLoading(true);
        v3Api.command.synergy(cid, { dimension: dim })
            .then(res => setFetched(res?.data || { pairs: [], topPair: null }))
            .catch(e => { setErr(e?.message || 'Failed'); setFetched({ pairs: [], topPair: null }); })
            .finally(() => setLoading(false));
    }, [dimension, loading]);

    const chipStyle = (isActive) => ({
        fontSize: 12,
        padding: '4px 10px',
        borderRadius: 999,
        border: '1px solid var(--v3-border, rgba(0,0,0,0.12))',
        background: isActive ? 'var(--v3-teal, #0ea5a4)' : 'transparent',
        color: isActive ? '#fff' : 'var(--v3-text-muted)',
        cursor: loading ? 'default' : 'pointer',
        opacity: loading && !isActive ? 0.5 : 1,
    });

    return (
        <div className="v3-card v3-cc-card">
            <div className="v3-cc-card-header-row">
                <h3>{t('Synergy Map')}</h3>
                <CardInfo title={t('About Synergy Map')}>
                    <p>
                        {t('This card surfaces')} <strong>{t('pairs whose daily sales move together')}</strong> — {t('useful for co-location, cross-promotion, and leasing-mix decisions.')}
                    </p>
                    <p>
                        <strong>{t('Grain')}</strong> {t('can be sub-category (default), category, or tenant. Most malls have only Retail and F&B at the category level, so sub-category reveals the real cross-shopping pairs.')}
                    </p>
                    <p>
                        <strong>{t('Correlation')}</strong> {t('is the Pearson correlation of the two series’ daily sales over the period, from −1 to +1. Values near +1 mean they rise and fall together.')}
                    </p>
                    <p>
                        <strong>{t('Top pair')}</strong> {t('is the highest-correlated pair and is highlighted at the top.')}
                    </p>
                    <p>
                        {t('Correlation is not causation — pairs may simply share mall footfall (weekends, paydays, holidays).')}
                    </p>
                </CardInfo>
            </div>

            {/* Grain toggle */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {GRAINS.map(g => (
                    <button
                        key={g.key}
                        type="button"
                        onClick={() => switchGrain(g.key)}
                        disabled={loading}
                        style={chipStyle(dimension === g.key)}
                    >
                        {t(g.label)}
                    </button>
                ))}
            </div>

            {top && (
                <div className="v3-cc-card-meta" style={{ marginBottom: 8 }}>
                    {t('Top pair')}: <strong style={{ color: 'var(--v3-teal)' }}>{top.a} ↔ {top.b}</strong>
                    {' '}· {t('corr')} {Number(top.correlation || 0).toFixed(2)}
                </div>
            )}

            {loading ? (
                <div className="v3-text-muted" style={{ fontSize: 13 }}>{t('Computing…')}</div>
            ) : pairs.length === 0 ? (
                <div className="v3-text-muted" style={{ fontSize: 13 }}>
                    {err ? String(err) : t('No correlation pairs computed.')}
                </div>
            ) : (
                <div className="v3-cc-synergy-list">
                    {pairs.map((p, i) => {
                        const key = `${p.a}|${p.b}`;
                        const isTop = topKey && key === topKey;
                        const corr = Number(p.correlation || 0);
                        const pct = Math.max(0, Math.min(1, Math.abs(corr))) * 100;
                        return (
                            <div key={i} className={`v3-cc-synergy-row ${isTop ? 'top' : ''}`}>
                                <span className="pair">{p.a} ↔ {p.b}</span>
                                <span className="bar"><div style={{ width: `${pct}%` }} /></span>
                                <span className="val">
                                    {corr.toFixed(2)}
                                    {p.lift_pct != null && <span style={{ opacity: 0.6 }}> · {Number(p.lift_pct).toFixed(0)}%</span>}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
